/**
 * Utility functions for feature access control
 */

// Define the products that can be enabled/disabled
export type ProductFlag =
  | 'email-analytics'
  | 'email-analytics-v2'
  | 'penny'
  | 'new-payroll-testing'
  | 'penny2'
  | 'chat'
  | 'compliance'
  | 'integrations';

// Legacy feature flags for backward compatibility within email analytics
export type FeatureFlag = 'analytics' | 'companies' | 'alerts' | 'autopayroll';

/**
 * Check if a product is available based on tenant configuration
 * @deprecated Use useTenantProductConfigContext().isProductEnabled() in React components instead
 * This function is kept for backward compatibility but will always return false
 * as products are now controlled via tenant_config table only
 */
export function isProductEnabled(product: ProductFlag): boolean {
  // Products are now controlled via tenant_config table only
  // This function returns false to maintain backward compatibility
  // Use useTenantProductConfigContext().isProductEnabled() in React components
  return false;
}

/**
 * Compliance is open to super_admin, tenant admin, user, and company manager.
 * Users and managers see the live pages only (path allowlist). Tenant product
 * enablement still controls suite visibility.
 */
export function canAccessComplianceRoutes(role: string | null | undefined): boolean {
  return (
    role === 'super_admin' ||
    role === 'admin' ||
    role === 'user' ||
    role === 'manager'
  );
}

export function canAccessComplianceProduct({
  role,
  isProductEnabled,
}: {
  role: string | null | undefined;
  isProductEnabled: boolean;
}): boolean {
  return canAccessComplianceRoutes(role) && isProductEnabled;
}

export function isManagerRole(role: string | null | undefined): boolean {
  return role === 'manager';
}

/** Users get the live Compliance pages. Admins are not limited. */
export function isLimitedComplianceRole(role: string | null | undefined): boolean {
  return role === 'user';
}

const MANAGER_ALLOWED_PATHS: RegExp[] = [
  /^\/$/,
  /^\/unauthorized\/?$/,
  /^\/mfa(\/|$)/,
  /^\/compliance\/?$/,
  /^\/compliance\/regulations(\/|$)/,
  /^\/compliance\/handbooks(\/|$)/,
  /^\/compliance\/companies(\/|$)/,
  /^\/compliance\/bank(\/|$)/,
];

/** Live Compliance pages plus the product suite. Catalog, ETL, and other products are denied. */
export function canManagerAccessPath(pathname: string): boolean {
  return MANAGER_ALLOWED_PATHS.some((pattern) => pattern.test(pathname));
}

/** Admins may open any Compliance path. Users and managers are limited to live pages. */
export function canRoleAccessCompliancePath(
  role: string | null | undefined,
  pathname: string,
): boolean {
  if (!canAccessComplianceRoutes(role)) {
    return false;
  }
  if (isManagerRole(role) || isLimitedComplianceRole(role)) {
    return canManagerAccessPath(pathname);
  }
  return true;
}

/**
 * Integrations follows the Compliance model: super admins only. Tenant product
 * enablement controls whether super admins see the product in the suite; it does
 * not grant access to other roles.
 */
export function canAccessIntegrationsRoutes(isSuperAdmin: boolean): boolean {
  return isSuperAdmin;
}

export function canAccessIntegrationsProduct({
  isSuperAdmin,
  isProductEnabled,
}: {
  isSuperAdmin: boolean;
  isProductEnabled: boolean;
}): boolean {
  return isSuperAdmin && isProductEnabled;
}

/**
 * Check if PennyV3 (penny2) should be hidden from non-super-admin users
 */
export function isPennyV3Hidden(): boolean {
  const envRecord = import.meta.env as unknown as Record<string, string | boolean | undefined>;
  const rawValue = envRecord.VITE_HIDE_PENNY2;
  return String(rawValue).toLowerCase() === 'true';
}

/**
 * Legacy function for backward compatibility - maps old feature flags to new product flags
 * @deprecated Use isProductEnabled instead
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  switch (feature) {
    case 'analytics':
    case 'companies':
    case 'alerts':
      // These are all part of the email analytics product
      return isProductEnabled('email-analytics');
    case 'autopayroll':
      return isProductEnabled('penny');
    default:
      return false;
  }
}

/**
 * Get a list of all enabled products
 * @deprecated Use useTenantProductConfigContext().getEnabledProducts() in React components instead
 * This function is kept for backward compatibility but will always return an empty array
 * as products are now controlled via tenant_config table only
 */
export function getEnabledProducts(): ProductFlag[] {
  // Products are now controlled via tenant_config table only
  // This function returns empty array to maintain backward compatibility
  // Use useTenantProductConfigContext().getEnabledProducts() in React components
  return [];
}

/**
 * Get a list of all enabled features (legacy)
 * @deprecated Use getEnabledProducts instead
 */
export function getEnabledFeatures(): FeatureFlag[] {
  const features: FeatureFlag[] = [];
  
  if (isProductEnabled('email-analytics')) {
    features.push('analytics', 'companies', 'alerts');
  }
  
  if (isProductEnabled('penny')) {
    features.push('autopayroll');
  }
  
  return features;
}

/**
 * Check if Email Chat is enabled via env flag
 */
export function isEmailChatEnabled(): boolean {
  const envRecord = import.meta.env as unknown as Record<string, string | boolean | undefined>;
  // Prefer Vite-exposed variable, fallback to non-prefixed if present
  const rawValue = envRecord.VITE_ACTIVATE_CHAT_EMAIL ?? envRecord.activate_chat_email;
  return String(rawValue).toLowerCase() === 'true';
}

/**
 * Check if All Emails page should be shown
 */
export function isShowAllEmailsEnabled(): boolean {
  const envRecord = import.meta.env as unknown as Record<string, string | boolean | undefined>;
  const rawValue = envRecord.VITE_SHOW_ALL_EMAILS;
  return String(rawValue).toLowerCase() === 'true';
}

/**
 * Env-only check for chat (Vite build-time). Product access is normally controlled via
 * `tenant_config` (`product_chat_enabled`) and `useTenantProductConfig` / `isProductEnabled('chat')`,
 * with env fallbacks for tenants with no config rows.
 */
export function isChatEngineEnabled(): boolean {
  const envRecord = import.meta.env as unknown as Record<string, string | boolean | undefined>;
  const rawValue = envRecord.VITE_USE_CHAT ?? envRecord.vite_use_chat;
  return String(rawValue).toLowerCase() === 'true';
}
